import {
  Button,
  Divider,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { IconInfoCircle, IconReceipt } from "@tabler/icons-react";
import { Frequency, type PlanConfig } from "core";
import { formatCents } from "../../utils/amount";
import { trpc } from "../../utils/trpc";
import { InvoiceBreakdown } from "./InvoiceBreakdown";
import classes from "./PricingModal.module.css";

export function ResubscribeModal({
  plan,
  opened,
  close,
  onConfirm,
  isLoading = false,
}: {
  plan?: PlanConfig;
  isLoading?: boolean;
  opened: boolean;
  close: () => void;
  onConfirm: () => void;
}) {
  const {
    data: preview,
    isPending: isLoadingPreview,
    isError: isPreviewError,
  } = trpc.account.previewResubscribe.useQuery(
    {
      planSlug: plan?.planSlug ?? "",
      interval: plan?.interval ?? Frequency.Month,
    },
    { enabled: opened && !!plan },
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size="md"
      radius="lg"
      title="Checkout"
    >
      <Stack gap="lg">
        <Group justify="center" pt="sm">
          <ThemeIcon
            size={72}
            radius="xl"
            variant="light"
            color="orange"
            className={classes.receiptIcon}
          >
            <IconReceipt size={32} />
          </ThemeIcon>
        </Group>

        <Stack gap="sm">
          <Group justify="space-between" align="baseline">
            <Text c="dimmed">Plan</Text>
            <Text>{plan?.name}</Text>
          </Group>

          {isLoadingPreview && !isPreviewError && (
            <>
              <Skeleton height={16} radius="sm" />
              <Skeleton height={16} radius="sm" />
            </>
          )}

          {isPreviewError && (
            <Text size="sm" c="red">
              We couldn't calculate your total. Make sure you have a payment
              method and billing address on file, or contact support.
            </Text>
          )}

          {preview && <InvoiceBreakdown {...preview} />}

          <Divider />

          <Group justify="space-between" align="baseline">
            <Text fw={800}>Total</Text>
            {preview ? (
              <Group gap={6} align="baseline">
                <Text className={classes.priceAmount}>
                  {formatCents(preview.total, preview.currency)}
                </Text>
                <Text size="sm" className={classes.priceFrequency}>
                  /{plan?.interval}
                </Text>
              </Group>
            ) : (
              !isPreviewError && <Skeleton height={32} width={120} />
            )}
          </Group>
        </Stack>

        <Stack gap={6} className={classes.disclaimer}>
          <Group gap={6} wrap="nowrap" align="flex-start">
            <IconInfoCircle size={16} className={classes.disclaimerIcon} />
            <Text size="xs" c="dimmed">
              Have a promo code? No problem, just let us know after subscribing
              and we'll add it to your account!
            </Text>
          </Group>
          <Group gap={6} wrap="nowrap" align="flex-start">
            <IconInfoCircle size={16} className={classes.disclaimerIcon} />
            <Text size="xs" c="dimmed">
              Your registered payment method in your NOAN account will be
              charged for this transaction. If you don't possess a NOAN account
              or encounter any issues, please contact our support team.
            </Text>
          </Group>
        </Stack>

        <Group gap="sm" justify="flex-end">
          <Button onClick={close} variant="default">
            Cancel
          </Button>
          <Button loading={isLoading} onClick={onConfirm}>
            Subscribe
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
